import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import { 
  Avatar, 
  Box, 
  Button, 
  Divider, 
  IconButton, 
  Menu, 
  MenuItem, 
  Typography 
} from '@mui/material';
import { AccountCircle } from '@mui/icons-material';
import { useAuthProfileRetrieveQuery, useAuthLogoutCreateMutation, useAuthProfileUpdateMutation } from '../../../services/GLOWClient';
import { useTranslation } from 'react-i18next';

const ProfileDropdown = () => {
  const navigate = useNavigate();
  const { logout, user, isAuthenticated } = useAuth();
  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = useState(null);
  const { data: profileData, isLoading, refetch } = useAuthProfileRetrieveQuery(undefined, { skip: !isAuthenticated() });
  const [updateProfile, { isLoading: isUpdating }] = useAuthProfileUpdateMutation();
  

  // Refetch profile data when user changes
  useEffect(() => {
    if (user) {
      refetch();
    }
  }, [user, refetch]);
  
  
  const handleMenu = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = async () => {
    try {
      // Use the AuthContext's logout function which handles both API call and local state
      await logout();
      // Redirection handled by ProtectedRoute
    } catch (error) {
      console.error('Logout failed:', error);
    } finally {
      handleClose();
    }
  };

  return (
    <Box>
      <IconButton
        size="large"
        aria-label="account of current user"
        aria-controls="menu-appbar"
        aria-haspopup="true"
        onClick={handleMenu}
        color="inherit"
        sx={{ 
          opacity: user ? 1 : 0.6,
          '&:hover': {
            opacity: 1
          }
        }}
      >
        <AccountCircle />
      </IconButton>
      <Menu
        id="menu-appbar"
        anchorEl={anchorEl}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        keepMounted
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        open={Boolean(anchorEl)}
        onClose={handleClose}
      >
        <Box sx={{ p: 2, minWidth: 250 }}>
          {!user ? (
            <Typography variant="body2" color="text.secondary">
              {t('client_profile.not_connected')}
            </Typography>
          ) : (
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <Avatar sx={{ mr: 2 }}>
                {profileData?.first_name?.[0] || 'U'}
              </Avatar>
              <Box>
                <Typography variant="subtitle1">
                  {isLoading
                    ? t('common.loading')
                    : `${profileData?.first_name || ''} ${profileData?.last_name || ''}`.trim() || t('client_profile.user_fallback')}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {profileData?.email || t('common.loading')}
                </Typography>
              </Box>
            </Box>
          )}
        </Box>
        <Divider />
        {!user ? (
          <MenuItem component={Link} to="/auth/login" onClick={handleClose}>
            {t('actions.login')}
          </MenuItem>
        ) : (
          <>
            <MenuItem component={Link} to="/client/profile" onClick={handleClose}>
              {t('user.profile')}
            </MenuItem>
            <MenuItem component={Link} to="/client/appointments" onClick={handleClose}>
              {t('client_profile.my_appointments')}
            </MenuItem>

            <Divider />
            <MenuItem onClick={handleLogout}>
              {t('actions.logout')}
            </MenuItem>
          </>
        )}
      </Menu>
    </Box>
  );
};

export default ProfileDropdown;